import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useFiltersQuery from "../api/use-filters-query";
import { useAppliedFilterStore, useSelectedFilterStore } from "../store/filtersv2";
import { defaultFilters, newSearchFilters } from "../store/filtersv2/defaults";
import { BasicFilterOption, FiltersResponse } from "../types";
import { SortBy } from "../types/enums/sort-by";
import { Filters, Queries } from "../types/filters";
import { UserLocationContext } from "../UserLocation/UserLocationContext";
import { getQueryParamsObj } from "./get-query-params-obj";
import { useDealersStore } from "./use-dealers-store";

export const useFiltering = () => {

  const [ checkedURLParams, setCheckedURLParams ] = useState(false);

  const { postalChecked, postalCode, parseLocationParams } = useContext(UserLocationContext);
  const { data } = useFiltersQuery();
  const { applied, applyFilter, applyFilters } = useAppliedFilterStore();
  const { selected, selectFilter, selectFilters } = useSelectedFilterStore();
  const { dealersWithinRadius } = useDealersStore();

  const navigate = useNavigate();
  const location = useLocation();
  const [ searchParams, _setSearchParams ] = useSearchParams();

  const parseURLParams = () => {
    if (!checkedURLParams) {
      const queryParams: { [key: string]: any } = {};
      const options = data as FiltersResponse;

      for (const [key, value] of searchParams) {
        if (Object.keys(options).includes(key)) {
          switch (key) {
            case "distance":
            case "price":
            case "mileage":
              queryParams[key] = Number(value);
              break;
            case "in_stock":
              queryParams[key] = value == "true" ? true : false;
              break;
            default:
              const optionCount = (options[key as keyof FiltersResponse] as BasicFilterOption[]).length;
              const valArr = value.split(/[,:]/g);
              queryParams[key] = valArr.length === optionCount ? [] : valArr;
              break;
          }
        } else {
          switch (key) {
            case "dealer":
              const dealerCount = dealersWithinRadius?.length;
              const valArr = value.split(/[,:]/g);
              queryParams[key] = valArr.length === dealerCount ? [] : valArr;
              break;
            case "sort_price":
              queryParams.sort_price = value;
              queryParams.sort_added = undefined;
              queryParams.sort_mileage = undefined;
              if (value === "asc") {
                queryParams.sort_by = SortBy.LOWEST_PRICE;
              } else if (value === "desc") {
                queryParams.sort_by = SortBy.HIGHEST_PRICE;
              }
              break;
            case "sort_added":
              queryParams.sort_price = undefined;
              queryParams.sort_added = "asc";
              queryParams.sort_mileage = undefined;
              queryParams.sort_by = SortBy.NEWLY_ADDED;
              break;
            case "sort_mileage":
              queryParams.sort_price = undefined;
              queryParams.sort_added = undefined;
              queryParams.sort_mileage = "asc";
              queryParams.sort_by = SortBy.LOWEST_MILEAGE;
              break;
            default:
              queryParams[key] = value;
            break;
          }
        }
      }

      applyFilters(queryParams as Filters);
      selectFilters(queryParams as Filters);
      // setSearchParams ({}, {replace : true});
      setCheckedURLParams(true);
    }
  };

  const getQueryParams = useCallback((endpoint: string, options?: {
    returnString?: boolean,
    useApplied?: boolean
  }) => {
    const filters = options?.useApplied ? applied : selected;
    const queries: Queries = {
      postal_code: postalCode,
      ...getQueryParamsObj(endpoint, filters)
    };

    const params = new URLSearchParams(queries);

    if (options?.returnString) {
      return params.toString();
    }

    return params;
  }, [postalCode, applied, selected]);

  const startNewSearch = () => {
    const filters = {
      ...newSearchFilters,
      category: applied?.category,
    }
    applyFilters(filters),
    selectFilters(filters)
  };

  const applyQuickFilter = <T,>(filter: keyof Filters, value: T) => {
    selectFilter(filter, value);
    applyFilter(filter, value);
  };

  const applySelectedFilters = () => {
    applyFilters(selected);
  };

  const selectAppliedFilters = useCallback(() => {
    selectFilters(applied);
  }, [applied]);

  const resetSelectedFilters = () => {
    selectFilters(defaultFilters);
  };

  useEffect(() => {
    if (checkedURLParams && location.pathname.includes("results")) {
      const params = getQueryParams("url", {returnString: true, useApplied: true}) as string;
      navigate({
        pathname: '/results',
        search: params,
      }, {
        replace: true,
        state: {
          from: location.pathname
        }
      });
      // setSearchParams(params, { replace: true, state: { from: location.pathname } });
    }
  }, [postalCode, applied, checkedURLParams]);

  useEffect(() => {
    if (postalChecked && parseLocationParams && data) {
      parseURLParams();
      parseLocationParams();
    };
  }, [postalChecked, parseLocationParams, data]);

  return {
    applyQuickFilter,
    applySelectedFilters,
    selectAppliedFilters,
    resetSelectedFilters,
    startNewSearch,
    getQueryParams,
    checkedURLParams
  };
};
